/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { type BarRowItem, buildBarRowSeries } from '@/components/charts/history/barRow'
import { formatDurationLong } from '@/utils/formatDate'

import { MOVEMENT_COLOR, defaultPalette, labelColorFor, resolveColor, resolveLabel } from './palette'
import {
  REEFER_DETAIL_ROWS,
  type StatusRow,
  buildStateSegments,
  geofenceNames,
  geofenceRowKey,
  reeferRowKey,
} from './statusRows'
import type { AssetHistory } from './types'
import type { StatusPlacement } from './useChartOptions'

type PlacementFor = (key: string) => StatusPlacement | null
type FormatTime = (value: number) => string

/** Bar series for the reefer detail rows (Power Mode … Air Control), one run-length bar per state. */
export const buildReeferDetailSeries = (
  data: AssetHistory,
  placementFor: PlacementFor,
  visEnd: number,
  fmtTime: FormatTime
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any[] =>
  REEFER_DETAIL_ROWS.flatMap(({ field, label }) => {
    const placement = placementFor(reeferRowKey(field))
    if (!placement) return []
    const segments = buildStateSegments(data[field] ?? [], visEnd)
    if (!segments.length) return []
    const items: BarRowItem[] = segments.map((seg) => ({ value: [seg.start, seg.end], _value: seg.value }))
    return [
      buildBarRowSeries({
        ...placement,
        name: label,
        data: items,
        colorFor: (i) => resolveColor(defaultPalette, field, segments[i].value),
        label: (i) => ({
          text: resolveLabel(defaultPalette, field, segments[i].value),
          color: labelColorFor(resolveColor(defaultPalette, field, segments[i].value)),
        }),
        tooltip: (d: BarRowItem) =>
          [
            `<b>${label}</b>: ${resolveLabel(defaultPalette, field, d._value)}`,
            `${fmtTime(d.value[0])} – ${fmtTime(d.value[1])}`,
            `Duration: ${formatDurationLong(d.value[0], d.value[1])}`,
          ].join('<br/>'),
      }),
    ]
  })

/** Bar series for the per-geofence rows: one bar per visit, an open visit running to `visEnd`. */
export const buildGeofenceRowSeries = (
  data: AssetHistory,
  hideGeofences: boolean,
  placementFor: PlacementFor,
  visEnd: number,
  fmtTime: FormatTime
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any[] =>
  geofenceNames(data, hideGeofences).flatMap((name) => {
    const placement = placementFor(geofenceRowKey(name))
    if (!placement) return []
    const items: BarRowItem[] = (data.geofences ?? [])
      .filter((visit) => visit.name === name)
      .map((visit) => {
        const entry = new Date(visit.entryTime).getTime()
        const exit = visit.exitTime ? new Date(visit.exitTime).getTime() : null
        return { value: [entry, exit ?? visEnd], _entry: entry, _exit: exit }
      })
    return [
      buildBarRowSeries({
        ...placement,
        name: `__geofence_row__:${name}`,
        data: items,
        colorFor: () => MOVEMENT_COLOR,
        tooltip: (d: BarRowItem) =>
          [
            `<b>${name}</b>`,
            `Entered: ${fmtTime(d._entry)}`,
            d._exit == null ? 'Still inside' : `Exited: ${fmtTime(d._exit)}`,
            `Duration: ${formatDurationLong(d._entry, d._exit ?? Date.now())}`,
          ].join('<br/>'),
      }),
    ]
  })

/**
 * The category y-axis of the grid under the graph: one row per StatusRow, first at the top.
 * Category values are the row keys (unique even when a geofence shares a name with a lane);
 * the formatter shows the label, with section headers bold and marked ▸/▾. `triggerEvent`
 * lets a click on a header label toggle its section (AssetHistoryChart handles the click).
 */
export const buildStatusRowsAxis = (rows: StatusRow[]) => {
  const byKey = new Map(rows.map((row) => [row.key, row]))
  const expanded = new Set(rows.filter((row) => !row.header).map((row) => row.section))
  return {
    type: 'category',
    gridIndex: 1,
    inverse: true,
    data: rows.map((row) => row.key),
    boundaryGap: true,
    triggerEvent: true,
    axisTick: { show: false },
    axisLine: { show: false },
    splitLine: { show: false },
    axisLabel: {
      fontSize: 10,
      color: '#374151',
      width: 110,
      overflow: 'truncate',
      formatter: (key: string) => {
        const row = byKey.get(key)
        if (!row) return key
        return row.header ? `{header|${expanded.has(row.section) ? '▾' : '▸'} ${row.label}}` : row.label
      },
      rich: { header: { fontSize: 10, fontWeight: 'bold', color: '#111827' } },
    },
  }
}
